import React from 'react';
import { format } from 'date-fns';
import ProductPreview from './components/ProductPreview';
import OfferMessage from './components/OfferMessage';
import SwapRequestMessage from './components/SwapRequestMessage';
import FulfillmentContextMessage from './components/FulfillmentContextMessage';

// Helper to get a clean ID (non-empty string or null)
const getCleanId = (idVal) => {
  const idStr = idVal ? String(idVal).trim() : null;
  return idStr && idStr !== "" ? idStr : null;
};

const MessageItem = ({ message, currentUser, productContextForDisplay }) => {
  const formattedTime = message.created_at
    ? format(new Date(message.created_at), 'h:mm a')
    : '';
  
  const messageSenderObject = message.sender || message.messageSender; // Consolidate access to sender info
  const senderId = messageSenderObject?.id || (Array.isArray(messageSenderObject) ? messageSenderObject[0]?.id : null);
  const isMsgFromCurrentUser = !!(currentUser && senderId && getCleanId(senderId) === getCleanId(currentUser.id));
  
  // Determine message type based on content or properties
  const isOfferMessage = message.offerPrice || (message.status && message.status.type === 'offer');
  const isSwapRequest = message.requestedProduct || (message.status && message.status.type === 'swap');
  const isFulfillmentContext = message.messageType === 'fulfillment_context' || 
                              (message.fulfillmentGroup && 
                               (!message.messageText || message.messageText.startsWith('Order Context')));
  const isProductContextMessage = message.messageType === 'product_context' || !!productContextForDisplay;
  
  // Get profile picture URL
  const getProfilePicUrl = () => {
    // console.log('Message object:', message);
    // console.log('Message sender:', messageSenderObject);
    
    const user = messageSenderObject;
    
    // Check if user has a profilePic field that is an array with valid url
    if (user && 
        user.profilePic && 
        Array.isArray(user.profilePic) && 
        user.profilePic.length > 0 && 
        user.profilePic[0] && 
        user.profilePic[0].url) {
      return user.profilePic[0].url.startsWith('http') 
        ? user.profilePic[0].url 
        : `${user.profilePic[0].url}`;
    }
    
    // Check if user is an array (different structure)
    if (Array.isArray(user) && 
        user[0] && 
        user[0].profilePic && 
        Array.isArray(user[0].profilePic) && 
        user[0].profilePic.length > 0 && 
        user[0].profilePic[0] && 
        user[0].profilePic[0].url) {
      return user[0].profilePic[0].url.startsWith('http')
        ? user[0].profilePic[0].url
        : `${user[0].profilePic[0].url}`;
    }
    
    return null;
  };

  // Get display name
  const displayName = (Array.isArray(messageSenderObject) 
    ? (messageSenderObject[0]?.username || 'User')
    : (messageSenderObject?.username || 'User')) || 'User';
  
  // Render the appropriate message content based on type
  const renderMessageContent = () => {
    if (isFulfillmentContext) {
      // Extract the proper fulfillment group ID
      let fgId = message.fulfillmentGroup;
      
      // Handle if it's an object with an ID property
      if (typeof fgId === 'object' && fgId !== null) {
        fgId = fgId.id || fgId._id || null;
      }
      
      // Only render context message if we have a valid ID
      if (fgId) {
        return <FulfillmentContextMessage fulfillmentGroupId={fgId} />;
      } else {
        return <div className="text-sm text-gray-500">Order Context (Unable to load details)</div>;
      }
    }
    
    if (isProductContextMessage) {
      const productForPreview = productContextForDisplay || message.product;
      if (productForPreview && productForPreview.id) { // Ensure product has an id for the preview link
        return (
          <div>
            {message.messageText && !message.messageText.startsWith("Regarding product:") && (
              <div className="text-sm mb-1">{message.messageText}</div>
            )}
            <ProductPreview product={productForPreview} isSmall />
          </div>
        );
      } else if (message.messageText) { // Fallback to text if product details are not available
        return <div className="text-sm">{message.messageText}</div>;
      }
      return <div className="text-sm text-gray-500">Product Information (Details unavailable)</div>; 
    }
    
    if (isOfferMessage) {
      return (
        <OfferMessage 
          message={message} 
          isCurrentUser={isMsgFromCurrentUser} 
        />
      );
    } 
    
    if (isSwapRequest) {
      return (
        <SwapRequestMessage 
          message={message} 
          isCurrentUser={isMsgFromCurrentUser} 
        />
      );
    }

    return (
      <div>
        <div className="text-sm">{message.messageText}</div>
        {message.product && !isProductContextMessage && (
          <div className="mt-2">
            <ProductPreview product={message.product} isSmall />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`flex ${isMsgFromCurrentUser ? 'justify-end' : 'justify-start'} mb-2 items-end`}
    >
      {!isMsgFromCurrentUser && (
        <div className="flex-shrink-0 mr-2">
          {getProfilePicUrl() ? (
            <img 
              src={getProfilePicUrl()} 
              alt={displayName}
              className="w-8 h-8 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><rect width="100%" height="100%" fill="%2300BFA5"/><text x="50%" y="50%" font-family="Arial" font-size="16" fill="white" text-anchor="middle" dy=".3em">${displayName.charAt(0).toUpperCase()}</text></svg>`;
              }}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand-teal flex items-center justify-center text-white text-sm font-medium">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      )}

      <div
        className={`max-w-[75%] ${
          isMsgFromCurrentUser
            ? 'bg-brand-teal-light bg-opacity-20 text-gray-800'
            : 'bg-gray-100 text-gray-800'
        } rounded-lg px-4 py-3 shadow-sm ${
          isOfferMessage || isSwapRequest || isProductContextMessage ? 'border border-gray-200' : ''
        }`}
      >
        {renderMessageContent()}
        
        <div className="flex justify-end mt-2">
          <span className="text-xs text-gray-500">{formattedTime}</span>
          
          {isMsgFromCurrentUser && (
            <span className="ml-1 text-xs text-gray-500">
              {message.id && String(message.id).startsWith('temp-') 
                ? '⏳' 
                : (message.status?.read ? '✓✓' : '✓')}
            </span>
          )}
        </div>
      </div>

      {isMsgFromCurrentUser && (
        <div className="flex-shrink-0 ml-2">
          {getProfilePicUrl() ? (
            <img 
              src={getProfilePicUrl()} 
              alt={displayName}
              className="w-8 h-8 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><rect width="100%" height="100%" fill="%2300BFA5"/><text x="50%" y="50%" font-family="Arial" font-size="16" fill="white" text-anchor="middle" dy=".3em">${displayName.charAt(0).toUpperCase()}</text></svg>`;
              }}
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand-teal flex items-center justify-center text-white text-sm font-medium">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageItem; 